import React, { useState, useRef, useEffect } from 'react';

interface SmartDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  minWidth?: string;
  zIndex?: number;
  onToggle?: (isOpen: boolean) => void;
  closeOnItemClick?: boolean;
}

export const SmartDropdown: React.FC<SmartDropdownProps> = ({
  trigger,
  children,
  className = '',
  minWidth = '320px',
  zIndex = 2000,
  onToggle,
  closeOnItemClick = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const button = triggerRef.current;
    const buttonRect = button.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 400; // Altura máxima del dropdown

    // Verificar si hay espacio arriba o abajo
    const spaceAbove = buttonRect.top;
    const spaceBelow = viewportHeight - buttonRect.bottom;

    // Determinar la posición óptima considerando la altura máxima
    const newPosition = spaceBelow >= dropdownMaxHeight || spaceBelow > spaceAbove ? 'bottom' : 'top';
    setDropdownPosition(newPosition);
  };

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!isOpen) {
      calculatePosition();
    }
    
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onToggle?.(newIsOpen);
  };

  const renderChildrenWithClose = (nodes: React.ReactNode): React.ReactNode => {
    if (!closeOnItemClick) return nodes;

    return React.Children.map(nodes, (child) => {
      if (!React.isValidElement(child)) return child;

      const childProps: any = child.props || {};
      const existingOnClick = childProps.onClick;

      const wrappedOnClick = (event: React.MouseEvent) => {
        if (typeof existingOnClick === 'function') {
          existingOnClick(event);
        }
        setIsOpen(false);
        onToggle?.(false);
      };

      return React.cloneElement(child as React.ReactElement<any>, {
        ...childProps,
        onClick: wrappedOnClick,
      });
    });
  };

  // Cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onToggle?.(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  return (
    <div className="inline-block relative" style={{ zIndex }}>
      {/* Trigger */}
      <div ref={triggerRef} onClick={toggleDropdown}>
        {trigger}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute left-0 rounded-lg border border-gray-200 bg-white shadow-[0_4px_12px_rgba(0,0,0,0.15)] overflow-hidden block opacity-100 visible steps-scrollbar ${className} ${
            dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
          style={{
            minWidth,
            zIndex: zIndex + 1,
            maxHeight: '400px',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #ffffff',
          }}
        >
          {renderChildrenWithClose(children)}
        </div>
      )}
    </div>
  );
};
